import { Alert, Button, Card, Form, Select, Slider, Space } from "antd";
import { EditOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import type { SelectProps } from "antd/lib/select";

type DocumentRulesPanelProps = {
  smsEnabled: boolean;
  form?: FormInstance;
  formData?: any;
  formDisabled?: boolean;
  loading?: boolean;
  layout?: object;
  tailLayout?: object;
  documentOptions: SelectProps["options"];
  showEditControls: boolean;
  onToggleEdit: () => void;
  onChangeDocument: (value: any) => void;
  onValuesChange?: () => void;
  onSubmit?: (values: any) => void;
  showSubmit?: boolean;
  embedded?: boolean;
};

export const DocumentRulesPanelFields: React.FC<
  Pick<
    DocumentRulesPanelProps,
    "smsEnabled" | "documentOptions" | "onChangeDocument" | "formDisabled"
  >
> = ({ smsEnabled, documentOptions, onChangeDocument, formDisabled }) => {
  if (!smsEnabled) {
    return null;
  }

  return (
    <>
      <div className="card-content">
        <Form.Item
          name="ApplicationDocument"
          label="Application Document"
          rules={[{ required: true }]}
        >
          <Select
            onChange={onChangeDocument}
            id="ApplicationDocument"
            options={documentOptions}
            size="large"
            style={{ width: "100%" }}
            placeholder="Application Document"
          />
        </Form.Item>
      </div>
      <div className="card-content">
        <Form.Item
          name="PolicyDocument"
          label="Policy Document"
          rules={[{ required: true }]}
        >
          <Select
            onChange={onChangeDocument}
            id="PolicyDocument"
            options={documentOptions}
            size="large"
            style={{ width: "100%" }}
            placeholder="Policy Document"
          />
        </Form.Item>
      </div>
      <div>
        <Form.Item name="DaysToLapse" label="Days To Lapse">
          <Slider defaultValue={30} disabled={formDisabled} />
        </Form.Item>
      </div>
    </>
  );
};

const DocumentRulesPanel: React.FC<DocumentRulesPanelProps> = ({
  smsEnabled,
  form,
  formData,
  formDisabled = true,
  loading = false,
  layout = {},
  tailLayout = {},
  documentOptions,
  showEditControls,
  onToggleEdit,
  onChangeDocument,
  onValuesChange,
  onSubmit,
  showSubmit = false,
  embedded = false,
}) => {
  const fields = (
    <DocumentRulesPanelFields
      smsEnabled={smsEnabled}
      documentOptions={documentOptions}
      onChangeDocument={onChangeDocument}
      formDisabled={formDisabled}
    />
  );

  const submitButton =
    smsEnabled && showSubmit && !formDisabled ? (
      <div className="card-content">
        <Form.Item {...tailLayout}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button block size="large" htmlType="submit" loading={loading}>
              Submit
            </Button>
          </Space>
        </Form.Item>
      </div>
    ) : null;

  if (embedded) {
    return (
      <>
        {!smsEnabled && (
          <Alert
            type="info"
            showIcon
            message="SMS is not enabled for this group"
            description="Document assignment and lapse rules apply when SMS is enabled."
            style={{ marginBottom: 16 }}
          />
        )}
        <Card
          title="Document Assignment & Rules"
          bordered
          hoverable
          extra={
            showEditControls ? (
              <EditOutlined onClick={onToggleEdit} style={{ color: "blue" }} />
            ) : null
          }
        >
          {fields}
          {submitButton}
        </Card>
      </>
    );
  }

  return (
    <>
      {!smsEnabled && (
        <Alert
          type="info"
          showIcon
          message="SMS is not enabled for this group"
          description="Document assignment and lapse rules apply when SMS is enabled."
          style={{ marginBottom: 16 }}
        />
      )}
      <Card
        title="Document Assignment & Rules"
        bordered
        hoverable
        extra={
          showEditControls ? (
            <EditOutlined onClick={onToggleEdit} style={{ color: "blue" }} />
          ) : null
        }
      >
        <Form
          {...layout}
          onFinish={onSubmit}
          initialValues={formData}
          disabled={formDisabled}
          form={form}
          onValuesChange={onValuesChange}
        >
          {fields}
          {submitButton}
        </Form>
      </Card>
    </>
  );
};

export default DocumentRulesPanel;
